import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { breakoutAPI } from '../../../services/api';
import { PRESET } from '../constants';
import {
  buildHistoryDetailDrawer,
  buildLiveDetailDrawer,
  mergeHistoryDetailDrawer,
} from '../utils/detailDrawer';
import { usePreparationFormatters } from './usePreparationFormatters';

export function usePreparationPhase() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [refreshTask, setRefreshTask] = useState(null);
  const [refreshError, setRefreshError] = useState('');
  const [detailDrawer, setDetailDrawer] = useState(null);
  const [detailHistory, setDetailHistory] = useState({ ticker: null, loading: false, data: [], error: '' });
  const [exportingImage, setExportingImage] = useState(false);
  const tableExportRef = useRef(null);
  const [exportingChart, setExportingChart] = useState(false);
  const chartExportRef = useRef(null);
  const [exportingDrawer, setExportingDrawer] = useState(false);
  const drawerExportRef = useRef(null);
  const drawerBodyRef = useRef(null);
  const [metadata, setMetadata] = useState(null);
  const [metadataError, setMetadataError] = useState('');
  const [philosophyOpen, setPhilosophyOpen] = useState(false);
  const [filters, setFilters] = useState({
    min_score: 70,
    market_regime: '',
    exclude_trigger_none: false,
    limit: 50,
  });

  const formatters = usePreparationFormatters(metadata);

  const loadMetadata = useCallback(async () => {
    try {
      setMetadataError('');
      const res = await breakoutAPI.getMetadata({ preset: PRESET });
      setMetadata(res.data);
    } catch (e) {
      setMetadata(null);
      setMetadataError(e?.response?.data?.detail || e.message || t('preparation.failedToLoad'));
    }
  }, [t]);

  const load = useCallback(async ({ forceRefresh = false, showLoading = true } = {}) => {
    if (showLoading) setLoading(true);
    setError('');
    try {
      const params = {
        preset: PRESET,
        min_score: filters.min_score,
        limit: filters.limit,
        exclude_trigger_none: filters.exclude_trigger_none,
        refresh_if_stale: true,
      };
      if (forceRefresh) params.force_refresh = true;
      if (filters.market_regime) params.market_regime = filters.market_regime;
      const res = await breakoutAPI.getCandidates(params);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || t('preparation.failedToLoad'));
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [filters, t]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = {
        preset: PRESET,
        days: 18,
        min_score: filters.min_score,
        exclude_trigger_none: filters.exclude_trigger_none,
      };
      if (filters.market_regime) params.market_regime = filters.market_regime;
      const res = await breakoutAPI.getHistory(params);
      setHistory(res.data?.history || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  useEffect(() => {
    load();
    loadHistory();
  }, [load, loadHistory]);

  useEffect(() => {
    const ticker = detailDrawer?.ticker;
    if (!ticker) {
      setDetailHistory({ ticker: null, loading: false, data: [], error: '' });
      return undefined;
    }
    let active = true;
    setDetailHistory({ ticker, loading: true, data: [], error: '' });
    breakoutAPI.getTickerHistory(ticker, { days: 60, preset: PRESET })
      .then((res) => {
        if (!active) return;
        setDetailHistory({ ticker, loading: false, data: res.data?.history || [], error: '' });
      })
      .catch((e) => {
        if (!active) return;
        setDetailHistory({ ticker, loading: false, data: [], error: e?.message || 'error' });
      });
    return () => { active = false; };
  }, [detailDrawer?.ticker]);

  useEffect(() => {
    if (!refreshTask?.taskId || refreshTask.ready) return undefined;

    let active = true;
    const intervalId = window.setInterval(async () => {
      try {
        const res = await breakoutAPI.getRefreshStatus(refreshTask.taskId);
        if (!active) return;

        const nextTask = res.data;
        setRefreshTask(nextTask);

        if (nextTask.ready) {
          window.clearInterval(intervalId);
          if (nextTask.successful) {
            load();
            loadHistory();
          } else if (nextTask.error) {
            setRefreshError(nextTask.error);
          }
        }
      } catch (pollError) {
        if (!active) return;
        window.clearInterval(intervalId);
        setRefreshError(pollError?.response?.data?.detail || pollError.message || t('preparation.refresh.failed'));
      }
    }, 3000);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [load, loadHistory, refreshTask, t]);

  const handleTriggerRefresh = useCallback(async () => {
    setRefreshError('');
    setRefreshLoading(true);
    setRefreshTask({
      taskId: null,
      status: 'STARTED',
      ready: false,
      successful: null,
      failed: null,
      score_date: null,
      preset: PRESET,
      result: null,
      error: null,
    });
    try {
      const res = await breakoutAPI.triggerRefresh({ preset: PRESET });
      if (res.data.task_id) {
        setRefreshTask({
          taskId: res.data.task_id,
          status: 'PENDING',
          ready: false,
          successful: null,
          failed: null,
          score_date: res.data.score_date,
          preset: res.data.preset,
          result: null,
          error: null,
        });
      } else {
        await Promise.all([
          load({ forceRefresh: true, showLoading: false }),
          loadHistory(),
        ]);
        setRefreshTask({
          taskId: null,
          status: 'SUCCESS',
          ready: true,
          successful: true,
          failed: false,
          score_date: res.data.score_date,
          preset: res.data.preset,
          result: null,
          error: null,
        });
      }
    } catch (triggerError) {
      const message = triggerError?.response?.data?.detail || triggerError.message || t('preparation.refresh.failed');
      setRefreshTask({
        taskId: null,
        status: 'FAILURE',
        ready: true,
        successful: false,
        failed: true,
        score_date: null,
        preset: PRESET,
        result: null,
        error: message,
      });
      setRefreshError(message);
    } finally {
      setRefreshLoading(false);
    }
  }, [load, loadHistory, t]);

  const candidates = useMemo(() => data?.candidates || [], [data?.candidates]);

  const stats = useMemo(() => {
    const prepBuy = candidates.filter((x) => x.signal === 'BUY_PREP').length;
    const cautionBuy = candidates.filter((x) => x.signal === 'BUY_CAUTION').length;
    const buy = prepBuy + cautionBuy;
    const watch = candidates.filter((x) => x.signal === 'BUY_WATCH').length;
    const triggered = candidates.filter((x) => x.trigger_type && x.trigger_type !== 'NONE').length;
    const regimeCount = candidates.reduce((acc, c) => {
      const k = c.market_regime || 'UNKNOWN';
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    const dominant = Object.entries(regimeCount).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    return { buy, prepBuy, cautionBuy, watch, triggered, regimeCount, dominant };
  }, [candidates]);

  const topScore = candidates[0]?.breakout_score ?? null;
  const avgScore = candidates.length
    ? candidates.reduce((sum, item) => sum + Number(item.breakout_score || 0), 0) / candidates.length
    : null;
  const refreshInFlight = refreshLoading || (refreshTask && !refreshTask.ready);
  const refreshSucceeded = Boolean(refreshTask?.ready && refreshTask?.successful);
  const refreshFailed = Boolean(refreshTask?.ready && refreshTask?.failed);
  const refreshStatusLabel = useMemo(() => {
    if (!refreshTask) return null;
    if (refreshTask.failed) return t('preparation.refresh.status.failed');
    if (refreshTask.successful) return t('preparation.refresh.status.completed');
    if (refreshTask.status === 'STARTED') return t('preparation.refresh.status.running');
    return t('preparation.refresh.status.queued');
  }, [refreshTask, t]);
  const historyItems = useMemo(() => (history || []).slice().sort((a, b) => (a.score_date < b.score_date ? 1 : -1)), [history]);
  const historyTone = useCallback((score) => {
    if (score >= 68) return '#16a34a';
    if (score >= 60) return '#2563eb';
    if (score >= 52) return '#b45309';
    return '#64748b';
  }, []);

  const openLiveDetail = useCallback((candidate) => {
    setDetailDrawer(buildLiveDetailDrawer(candidate, data?.score_date));
  }, [data?.score_date]);

  const openHistoryDetail = useCallback(async (pick, historyRow) => {
    setDetailDrawer(buildHistoryDetailDrawer(pick, historyRow));
    try {
      const res = await breakoutAPI.getCandidates({ score_date: historyRow.score_date, preset: PRESET });
      const list = res.data?.candidates || res.data?.data || [];
      const full = list.find((c) => c.ticker === pick.ticker);
      if (!full) {
        setDetailDrawer((prev) => (prev && prev.ticker === pick.ticker && prev.score_date === historyRow.score_date ? { ...prev, loadingDetail: false } : prev));
        return;
      }
      setDetailDrawer((prev) => mergeHistoryDetailDrawer(prev, pick, historyRow, full));
    } catch (_) {
      setDetailDrawer((prev) => (prev && prev.ticker === pick.ticker && prev.score_date === historyRow.score_date ? { ...prev, loadingDetail: false } : prev));
    }
  }, []);

  const handleExportTableImage = useCallback(async () => {
    if (!tableExportRef.current || exportingImage) return;
    try {
      setExportingImage(true);
      const canvas = await html2canvas(tableExportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement('a');
      const asOfDate = data?.score_date || new Date().toISOString().slice(0, 10);
      link.download = `preparation-phase-table-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setExportingImage(false);
    }
  }, [data?.score_date, exportingImage]);

  const handleExportChartImage = useCallback(async () => {
    if (!chartExportRef.current || exportingChart) return;
    try {
      setExportingChart(true);
      const canvas = await html2canvas(chartExportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement('a');
      const ticker = detailDrawer?.ticker || 'ticker';
      const asOfDate = data?.score_date || new Date().toISOString().slice(0, 10);
      link.download = `signal-history-${ticker}-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setExportingChart(false);
    }
  }, [data?.score_date, detailDrawer?.ticker, exportingChart]);

  const handleExportDrawerImage = useCallback(async () => {
    const wrapper = drawerExportRef.current;
    const body = drawerBodyRef.current;
    if (!wrapper || exportingDrawer) return;
    const savedWrapperHeight = wrapper.style.height;
    const savedBodyOverflow = body ? body.style.overflowY : '';
    const savedBodyMaxHeight = body ? body.style.maxHeight : '';
    const savedBodyHeight = body ? body.style.height : '';
    try {
      setExportingDrawer(true);
      wrapper.style.height = 'auto';
      if (body) {
        body.style.overflowY = 'visible';
        body.style.maxHeight = 'none';
        body.style.height = 'auto';
      }
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const canvas = await html2canvas(wrapper, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        windowHeight: wrapper.scrollHeight,
      });
      const link = document.createElement('a');
      const ticker = detailDrawer?.ticker || 'ticker';
      const asOfDate = detailDrawer?.score_date || data?.score_date || new Date().toISOString().slice(0, 10);
      link.download = `preparation-${ticker}-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      wrapper.style.height = savedWrapperHeight;
      if (body) {
        body.style.overflowY = savedBodyOverflow;
        body.style.maxHeight = savedBodyMaxHeight;
        body.style.height = savedBodyHeight;
      }
      setExportingDrawer(false);
    }
  }, [data?.score_date, detailDrawer?.ticker, detailDrawer?.score_date, exportingDrawer]);

  const regimeWarning = useMemo(() => {
    if (!stats.dominant || candidates.length === 0) return null;
    if (stats.dominant === 'RECOVERY') return t('preparation.warnings.recovery');
    if (stats.dominant === 'RISK_OFF') return t('preparation.warnings.riskOff');
    if (stats.dominant === 'RISK_ON' && candidates.length > 30) return t('preparation.warnings.riskOn');
    return null;
  }, [candidates.length, stats.dominant, t]);

  const applyFilters = useCallback(() => {
    load();
    loadHistory();
  }, [load, loadHistory]);

  return {
    ...formatters,
    loading,
    error,
    data,
    metadata,
    metadataError,
    philosophyOpen,
    setPhilosophyOpen,
    filters,
    setFilters,
    load,
    applyFilters,
    historyItems,
    historyLoading,
    historyTone,
    refreshTask,
    refreshError,
    refreshInFlight,
    refreshSucceeded,
    refreshFailed,
    refreshStatusLabel,
    handleTriggerRefresh,
    candidates,
    stats,
    topScore,
    avgScore,
    regimeWarning,
    detailDrawer,
    setDetailDrawer,
    detailHistory,
    openLiveDetail,
    openHistoryDetail,
    exportingImage,
    exportingChart,
    exportingDrawer,
    tableExportRef,
    chartExportRef,
    drawerExportRef,
    drawerBodyRef,
    handleExportTableImage,
    handleExportChartImage,
    handleExportDrawerImage,
  };
}
